import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { applyActionCode, checkActionCode, reload } from "firebase/auth";
import { CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import AuthShell from "../components/auth/AuthShell";
import { auth } from "../lib/firebase";
import { errorDev } from '../utils/logger.js';

export default function VerifyEmailAction() {
  const { t, ready: i18nReady } = useTranslation(["auth", "common"]);
  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback || key;
    try {
      const val = t(key);
      return val === key ? fallback || key : val;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback || key;
    }
  };

  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!oobCode) {
        if (!mounted) return;
        setStatus("error");
        setMessage(safeT("auth.errors.invalidActionCode", "The verification link is invalid or incomplete."));
        return;
      }

      try {
        await checkActionCode(auth, oobCode);
        await applyActionCode(auth, oobCode);

        if (auth.currentUser) {
          await reload(auth.currentUser);
        }

        if (!mounted) return;
        setStatus("success");
        setMessage(safeT("auth.emailVerifiedSuccess", "Your email has been verified successfully."));
      } catch (error) {
        if (!mounted) return;
        setStatus("error");

        if (error?.code === "auth/expired-action-code") {
          setMessage(safeT("auth.errors.expiredActionCode", "This verification link has expired. Request a new one from Settings."));
          return;
        }

        if (error?.code === "auth/invalid-action-code") {
          setMessage(safeT("auth.errors.invalidActionCode", "This verification link is invalid or has already been used."));
          return;
        }

        setMessage(safeT("auth.errors.actionCodeFailed", "Could not verify your email. Please request a new verification link."));
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [oobCode]);

  const content = useMemo(() => {
    if (status === "loading") {
      return (
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-4 flex items-start gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-green-700 dark:text-green-400 mt-0.5" />
          <p className="text-sm text-gray-700 dark:text-gray-200">{safeT("auth.verifyingEmail", "Verifying your email...")}</p>
        </div>
      );
    }

    if (status === "success") {
      return (
        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-700 dark:text-green-400 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{message}</p>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-700 dark:text-red-400 mt-0.5" />
        <p className="text-sm text-red-800 dark:text-red-200">{message}</p>
      </div>
    );
  }, [message, status]);

  return (
    <AuthShell
      title={safeT("auth.verifyEmailTitle", "Email Verification")}
      subtitle={safeT("auth.verifyEmailSubtitle", "Confirm your email to unlock protected features.")}
      helperLinks={[
        { to: "/login", label: safeT("auth.signIn", "Sign In") },
        { to: "/settings", label: safeT("auth.verifyEmail", "Go to Settings") },
      ]}
    >
      <div className="space-y-4">
        {content}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/farm" className="ui-btn-secondary w-full py-2.5 text-center">
            {safeT("common:back", "Back")}
          </Link>
          <Link to="/settings" className="ui-btn-primary w-full py-2.5 text-center">
            {safeT("auth.verifyEmail", "Go to Settings")}
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
